<template>
  <div
    v-if="visible"
    id="core-migration-banner"
  >
    <div class="core-migration-banner__row">
      <div class="core-migration-banner__text">
        <v-chip
          class="core-migration-banner__badge"
          color="primary"
          small
          text-color="white"
        >
          {{ tb('badge') }}
        </v-chip>
        <span>{{ tb('message') }}</span>
      </div>

      <div class="core-migration-banner__actions">
        <v-btn
          ref="cta"
          :href="requestAccessUrl"
          class="core-migration-banner__cta"
          dark
          rel="noopener"
          small
          target="_blank"
        >
          {{ tb('cta') }}
        </v-btn>

        <v-btn
          :aria-label="tb('dismiss')"
          class="core-migration-banner__close"
          flat
          icon
          small
          @click="dismiss"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import theme from '@/plugins/theme'

const STORAGE_KEY = 'MIGRATION_BANNER_DISMISSED'

// New dashboard's public "request access" page - reachable without logging
// in. Pulled out as a single constant because it's expected to move: at the
// time this banner was built the page doesn't exist yet (another workstream
// is building it in Lovable), so this is an agreed-upon URL, not something
// this banner probes or falls back from.
const REQUEST_ACCESS_URL = 'https://cypheme-admin.lovable.app/request-access'

export default {
  data: () => ({
    visible: true,
    requestAccessUrl: REQUEST_ACCESS_URL,
    // Banner-local language: 'en' | 'cn' | 'fr'. Never written to VueI18n's
    // global locale for 'fr' (the rest of the legacy UI only has en/cn).
    bannerLocale: 'en'
  }),

  created () {
    this.applyLocale()
    this.visible = !this.readDismissed()
  },

  mounted () {
    this.applyBrandColor()
  },

  methods: {
    tb (key) {
      // Banner-local lookup: pass the locale explicitly so 'fr' works
      // without switching the app-wide locale (vue-i18n 7: $t(key, locale)).
      return this.$t('Core.MigrationBanner.' + key, this.bannerLocale)
    },

    applyLocale () {
      // zh* -> cn, fr* -> fr, anything else -> en. Existing behavior for zh
      // is kept (global locale switched to cn); fr stays banner-local because
      // the app ships only en/cn bundles for every other page.
      try {
        const browserLang = (navigator.language || navigator.userLanguage || '').toLowerCase()
        if (browserLang.indexOf('zh') === 0) {
          this.bannerLocale = 'cn'
          this.$i18n.locale = 'cn'
        } else if (browserLang.indexOf('fr') === 0) {
          this.bannerLocale = 'fr'
        } else {
          this.bannerLocale = 'en'
          this.$i18n.locale = 'en'
        }
      } catch (e) {
        // navigator unavailable (unlikely) - keep whatever locale i18n has.
      }
    },

    readDismissed () {
      try {
        return window.localStorage.getItem(STORAGE_KEY) === '1'
      } catch (e) {
        // localStorage can throw in private/incognito modes.
        return false
      }
    },

    dismiss () {
      this.visible = false
      try {
        window.localStorage.setItem(STORAGE_KEY, '1')
      } catch (e) {
        // Ignore write failures (private mode, storage disabled, quota...).
        // Worst case the banner reappears next visit.
      }
    },

    applyBrandColor () {
      // `.v-btn.primary` is hijacked app-wide by a leftover Material
      // Dashboard template rule (compiled from src/styles) that hardcodes
      // purple (#9c27b0) with !important on a 2-class selector - that beats
      // Vuetify's own generated `.primary` theme rule (1 class, also
      // !important) on specificity alone, regardless of load order. Every
      // `color="primary"` v-btn in this app renders purple, not just this
      // one - confirmed via computed style, not guessed. Fixing that shared
      // stylesheet is out of scope for this banner, so instead read the
      // real brand color straight from the same source Vuetify itself reads
      // (src/plugins/theme.js) and force it on just this button with an
      // inline !important, which always wins regardless of specificity.
      const cta = this.$refs.cta && this.$refs.cta.$el
      if (!cta) return
      cta.style.setProperty('background-color', theme.primary, 'important')
      cta.style.setProperty('border-color', theme.primary, 'important')
    }
  }
}
</script>

<style>
#core-migration-banner {
  background: #eef7ee;
  border-bottom: 1px solid rgba(76, 175, 80, 0.35);
  padding: 8px 16px;
  width: 100%;
}

.core-migration-banner__row {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1200px;
}

.core-migration-banner__text {
  align-items: center;
  color: #495057;
  display: flex;
  flex: 1 1 auto;
  font-size: 13px;
  gap: 8px;
  line-height: 1.4;
  min-width: 0;
}

.core-migration-banner__badge {
  flex: 0 0 auto;
  font-weight: 500;
}

.core-migration-banner__actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 4px;
}

.core-migration-banner__actions .v-btn {
  margin: 0;
}

.core-migration-banner__close.v-btn--icon {
  box-sizing: border-box;
  height: 28px !important;
  padding: 0 !important;
  width: 28px !important;
}

@media (max-width: 600px) {
  #core-migration-banner {
    padding: 8px 12px;
  }

  .core-migration-banner__row {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .core-migration-banner__actions {
    justify-content: flex-end;
    width: 100%;
  }
}
</style>
